import { useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import logger from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * Hook for managing answer handling and submission
 * @param {string} sessionId - Session ID
 * @param {Object} currentAnswers - Current answers state
 * @param {Function} setCurrentAnswers - Set current answers
 * @param {Object} savingAnswers - Saving answers state
 * @param {Function} setSavingAnswers - Set saving answers
 * @param {Object} saveTimeouts - Save timeouts state
 * @param {Function} setSaveTimeouts - Set save timeouts
 * @param {Function} fetchSession - Function to refetch session
 * @returns {Object} Answer handling functions
 */
export const useAnswerHandling = ({
  sessionId,
  currentAnswers,
  setCurrentAnswers,
  savingAnswers,
  setSavingAnswers,
  saveTimeouts,
  setSaveTimeouts,
  fetchSession
}) => {
  // Submit answer to backend
  // Accept optional answerData parameter to avoid stale closure issues
  const submitAnswer = useCallback(async (questionId, answerDataOverride = null) => {
    // Use override if provided, otherwise get from currentAnswers
    const answerData = answerDataOverride || currentAnswers[questionId];
    
    if (!answerData || !answerData.question_id) {
      logger.warn(`No answer data for question ${questionId}`);
      return;
    }
    
    try {
      setSavingAnswers(prev => ({ ...prev, [questionId]: true }));
      
      logger.log(`💾 Submitting answer for question ${questionId}:`, answerData);
      
      const response = await axios.post(
        `${API_URL}/api/live-session/${sessionId}/answer`,
        answerData,
        { withCredentials: true }
      );
      
      if (response.data.success) {
        logger.log(`✅ Answer saved successfully for question ${questionId}`);
        // Don't refetch session - let real-time subscription handle updates
        // This prevents unnecessary API calls and ensures real-time sync works
        // Use toast ID to prevent duplicates
        toast.success('Answer saved', { id: `answer-${questionId}`, duration: 1500, icon: '✓' });
      }
    } catch (err) {
      logger.error('Error saving answer:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to save answer';
      toast.error(errorMessage, { id: `answer-error-${questionId}` });
      throw err; // Re-throw so caller knows save failed
    } finally {
      setSavingAnswers(prev => ({ ...prev, [questionId]: false }));
    }
  }, [sessionId, currentAnswers, setSavingAnswers]);

  // Handle answer change - Update local state and save immediately
  const handleAnswerChange = useCallback((questionId, field, value) => {
    // Update local state immediately for instant UI feedback
    setCurrentAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...(prev[questionId] || {}),
        [field]: value,
        question_id: questionId
      }
    }));
    
    // Clear any existing timeout
    if (saveTimeouts[questionId]) {
      clearTimeout(saveTimeouts[questionId]);
    }
    
    // For boolean/yes_no questions, save immediately (no typing delay needed)
    // For text/numeric, use shorter delay to avoid too many saves while typing
    const isBooleanType = field === 'answer' && (value === 'Yes' || value === 'No' || value === true || value === false);
    const delay = isBooleanType ? 100 : 300; // Faster for boolean, slightly longer for text
    
    const timeoutId = setTimeout(() => {
      submitAnswer(questionId);
    }, delay);
    
    // Store timeout for cleanup
    setSaveTimeouts(prev => ({
      ...prev,
      [questionId]: timeoutId
    }));
  }, [setCurrentAnswers, setSaveTimeouts, saveTimeouts, submitAnswer]);

  return {
    handleAnswerChange,
    submitAnswer
  };
};

